const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');

class Scratch3MadBotStudyBot {
    constructor (runtime) {
        this.runtime = runtime;

        this.studyData = '';
        this.lastAnswer = '';
        this.lastError = '';
        this.isThinking = false;

        this.language = 'English';
        this.answerStyle = 'Short';
        this.onlyFromData = 'on';

        // Change this if your PHP file path is different
        this.apiUrl = 'https://madbot.in/api/madbot-studybot.php';
    }

    getInfo () {
        return {
            id: 'madbotStudyBot',
            name: 'MadBot StudyBot',
            color1: '#F88A17',
            color2: '#D96E00',
            color3: '#9A4B00',

            blocks: [
                {
                    blockType: BlockType.LABEL,
                    text: 'Study Data'
                },
                {
                    opcode: 'setStudyData',
                    blockType: BlockType.COMMAND,
                    text: 'set study data to [DATA]',
                    arguments: {
                        DATA: {
                            type: ArgumentType.STRING,
                            defaultValue: 'Light dependent resistor changes resistance according to light intensity.'
                        }
                    }
                },
                {
                    opcode: 'addStudyData',
                    blockType: BlockType.COMMAND,
                    text: 'add study data [DATA]',
                    arguments: {
                        DATA: {
                            type: ArgumentType.STRING,
                            defaultValue: 'LED means Light Emitting Diode.'
                        }
                    }
                },
                {
                    opcode: 'clearStudyData',
                    blockType: BlockType.COMMAND,
                    text: 'clear study data'
                },
                {
                    opcode: 'getStudyData',
                    blockType: BlockType.REPORTER,
                    text: 'study data'
                },
                {
                    opcode: 'getStudyDataLength',
                    blockType: BlockType.REPORTER,
                    text: 'study data length'
                },

                {
                    blockType: BlockType.LABEL,
                    text: 'Ask StudyBot'
                },
                {
                    opcode: 'askStudyBot',
                    blockType: BlockType.COMMAND,
                    text: 'ask StudyBot [QUESTION] and wait',
                    arguments: {
                        QUESTION: {
                            type: ArgumentType.STRING,
                            defaultValue: 'What is LED?'
                        }
                    }
                },
                {
                    opcode: 'getAnswer',
                    blockType: BlockType.REPORTER,
                    text: 'StudyBot answer'
                },
                {
                    opcode: 'hasAnswer',
                    blockType: BlockType.BOOLEAN,
                    text: 'StudyBot has answer?'
                },

                {
                    blockType: BlockType.LABEL,
                    text: 'Settings'
                },
                {
                    opcode: 'setLanguage',
                    blockType: BlockType.COMMAND,
                    text: 'set StudyBot language to [LANGUAGE]',
                    arguments: {
                        LANGUAGE: {
                            type: ArgumentType.STRING,
                            menu: 'languageMenu',
                            defaultValue: 'English'
                        }
                    }
                },
                {
                    opcode: 'setAnswerStyle',
                    blockType: BlockType.COMMAND,
                    text: 'set answer style to [STYLE]',
                    arguments: {
                        STYLE: {
                            type: ArgumentType.STRING,
                            menu: 'styleMenu',
                            defaultValue: 'Short'
                        }
                    }
                },
                {
                    opcode: 'setOnlyFromData',
                    blockType: BlockType.COMMAND,
                    text: 'answer only from study data [MODE]',
                    arguments: {
                        MODE: {
                            type: ArgumentType.STRING,
                            menu: 'onOffMenu',
                            defaultValue: 'on'
                        }
                    }
                },

                {
                    blockType: BlockType.LABEL,
                    text: 'Status'
                },
                {
                    opcode: 'thinking',
                    blockType: BlockType.BOOLEAN,
                    text: 'StudyBot is thinking?'
                },
                {
                    opcode: 'getLastError',
                    blockType: BlockType.REPORTER,
                    text: 'last StudyBot error'
                }
            ],

            menus: {
                languageMenu: {
                    acceptReporters: true,
                    items: ['English', 'Hindi', 'Gujarati']
                },
                styleMenu: {
                    acceptReporters: true,
                    items: ['Short', 'Simple', 'Detailed']
                },
                onOffMenu: {
                    acceptReporters: true,
                    items: ['on', 'off']
                }
            }
        };
    }

    setStudyData (args) {
        this.studyData = this._cleanText(args.DATA);
        this.lastError = '';
    }

    addStudyData (args) {
        const newData = this._cleanText(args.DATA);

        if (!newData) return;

        if (this.studyData) {
            this.studyData += '\n' + newData;
        } else {
            this.studyData = newData;
        }

        this.lastError = '';
    }

    clearStudyData () {
        this.studyData = '';
        this.lastAnswer = '';
        this.lastError = '';
    }

    getStudyData () {
        return this.studyData;
    }

    getStudyDataLength () {
        return this.studyData.length;
    }

    setLanguage (args) {
        this.language = this._cleanText(args.LANGUAGE) || 'English';
    }

    setAnswerStyle (args) {
        this.answerStyle = this._cleanText(args.STYLE) || 'Short';
    }

    setOnlyFromData (args) {
        const mode = this._cleanText(args.MODE).toLowerCase();
        this.onlyFromData = mode === 'off' ? 'off' : 'on';
    }

    async askStudyBot (args) {
        const question = this._cleanText(args.QUESTION);

        this.lastAnswer = '';
        this.lastError = '';

        if (!this.studyData) {
            this.lastError = 'Please set study data first.';
            return;
        }

        if (!question) {
            this.lastError = 'Please enter a question.';
            return;
        }

        this.isThinking = true;

        try {
            const payload = {
                data: this.studyData,
                question: question,
                language: this.language,
                style: this.answerStyle,
                onlyFromData: this.onlyFromData
            };

            const response = await fetch(this.apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            let result = null;

            try {
                result = await response.json();
            } catch (e) {
                throw new Error('Invalid server response.');
            }

            if (!response.ok) {
                throw new Error(result && result.error ? result.error : 'Server error.');
            }

            if (result.error) {
                throw new Error(result.error);
            }

            this.lastAnswer = result.answer || '';
            this.lastError = '';
        } catch (error) {
            this.lastAnswer = '';
            this.lastError = error.message || 'Something went wrong.';
        } finally {
            this.isThinking = false;
        }
    }

    getAnswer () {
        return this.lastAnswer;
    }

    hasAnswer () {
        return this.lastAnswer.trim().length > 0;
    }

    thinking () {
        return this.isThinking;
    }

    getLastError () {
        return this.lastError;
    }

    _cleanText (value) {
        if (value === null || typeof value === 'undefined') return '';
        return String(value).trim();
    }
}

module.exports = Scratch3MadBotStudyBot;